import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { DiscoveryFailure } from '@/utils/discoveryFailureMessage'
import type { BehaviorScreen } from '@/utils/instrumentation'
import { BehaviorAction, BehaviorEventName, activeScreen, newAttemptId, trackEvent } from '@/utils/instrumentation'
import api from '@axios'

import type { ChatMessage, CoverageMatch, DiscoverySession } from './types'

export type { CoverageMatch, DiscoverySession, DiscoveryFailure }

export const useIvDiscoveryStore = defineStore('iv-discovery', () => {
  const session = ref<DiscoverySession | null>(null)
  const history = ref<DiscoverySession[]>([])

  const historyPagination = ref({
    current_page: 1,
    per_page: 25,
    total: 0,
    last_page: 1,
  })

  const loading = ref(false)
  const historyLoading = ref(false)
  const chatMessages = ref<ChatMessage[]>([])
  const chatLoading = ref(false)
  const pollTimedOut = ref(false)

  // A poll request itself failed (network error, 5xx, the 195s request timeout) -- distinct
  // from pollTimedOut, which means every poll answered but the session was still pending at
  // the deadline. Both stop the poll loop.
  const pollFailed = ref(false)
  let pollTimer: ReturnType<typeof setTimeout> | null = null
  let pollDeadlineAt: number | null = null

  // Bumped whenever the poll loop is torn down (start()/selectSession()/reset()) so an
  // in-flight poll() request that resolves after the teardown can detect it's stale and no-op,
  // instead of overwriting session.value or re-scheduling itself with a fresh deadline.
  let pollGeneration = 0

  // How long to keep polling a 'pending' discovery session before giving up and surfacing an
  // error instead of spinning silently forever. 150s comfortably covers Stedi's own up-to-130s
  // discovery latency (see the same convention in StediService::pollDiscovery()'s server-side
  // docblock) plus one poll round trip.
  const POLL_DEADLINE_MS = 150_000

  // Bumped by every history load, so a load that resolves after a newer one was started drops its
  // page instead of overwriting or appending to the newer load's history.
  let historyGeneration = 0

  async function fetchHistory(scope: 'mine' | 'all' = 'mine', page = 1) {
    const generation = ++historyGeneration

    historyLoading.value = true
    try {
      const { data } = await api.get('/insurance/discover', {
        params: { scope, page, per_page: 25 },
      })

      if (generation !== historyGeneration)
        return

      const newData = data.data ?? data

      // Reset on page 1, append on subsequent pages (infinite scroll)
      if (page === 1)
        history.value = newData
      else
        history.value.push(...newData)

      if (data.meta) {
        historyPagination.value = {
          current_page: data.meta.current_page,
          per_page: data.meta.per_page,
          total: data.meta.total,
          last_page: data.meta.last_page,
        }
      }
    } finally {
      if (generation === historyGeneration)
        historyLoading.value = false
    }
  }

  async function selectSession(s: DiscoverySession) {
    // Switching sessions walks away from an in-flight run just as surely as a
    // timeout does.
    abandonDiscoveryRun()
    clearPoll()
    pollGeneration++
    chatMessages.value = []
    pollTimedOut.value = false
    pollFailed.value = false
    pollDeadlineAt = null

    // The paginated list endpoint omits `coverages` (only the detail endpoint
    // includes it), so a session picked from history needs a full refetch
    // before it can be displayed — otherwise coverages-dependent UI reads
    // undefined.
    if (s.status === 'complete' && s.coverages === undefined) {
      session.value = s
      await poll(s.uuid)

      return
    }

    session.value = s
    if (s.status === 'pending')
      schedulePoll(s.uuid)
  }

  // Funnel 1 (VH-2672). A discovery run spans start() and the poll loop, so
  // its screen and start time have to outlive the start() call. Settled once:
  // a run completes, times out, or errors, never more than one of those.
  let runScreen: BehaviorScreen | null = null
  let runStartedAt = 0
  let runSettled = true

  // Minted with each run and carried by all of its events (VH-2811). State,
  // like the screen and the clock, because the run outlives start().
  let runAttemptId = ''

  function beginDiscoveryRun() {
    // Settle whatever is still open before overwriting its screen and clock.
    // Starting a second run without this stranded the first one on
    // action_started forever, which reads in the funnel as a user who began a
    // discovery and vanished — the exact shape we are trying to measure.
    abandonDiscoveryRun()

    runScreen = activeScreen()
    runStartedAt = performance.now()
    runSettled = false
    runAttemptId = newAttemptId()

    if (runScreen)
      trackEvent(BehaviorEventName.ActionStarted, runScreen, { action: BehaviorAction.DiscoveryRun, attemptId: runAttemptId })
  }

  /**
   * Only reached once the run has actually settled, so 'pending' cannot arrive
   * here — which leaves 'complete' and 'error'. Note this is a different
   * vocabulary from verification's: DiscoverySession.status is
   * 'pending' | 'complete' | 'error', not 'success' | 'failed' | 'incomplete'.
   *
   * The conditional is required, not stylistic: `metadata: { result: status }`
   * is rejected by no-non-literal-track-event-metadata even though `status` is
   * typed to exactly these values, because the rule has no type information —
   * and "typed safely today" is the assumption that stops holding when a type
   * widens later.
   */
  function completeDiscoveryRun(status: DiscoverySession['status']) {
    if (runSettled || !runScreen)
      return

    runSettled = true

    const durationMs = Math.round(performance.now() - runStartedAt)
    const screen = runScreen

    trackEvent(BehaviorEventName.ActionCompleted, screen, {
      action: BehaviorAction.DiscoveryRun,
      metadata: { result: status === 'error' ? 'error' : 'complete' },
      attemptId: runAttemptId,
      durationMs,
    })
  }

  /** A run the user started and never got an answer from: timeout, or error. */
  function abandonDiscoveryRun() {
    if (runSettled || !runScreen)
      return

    runSettled = true

    trackEvent(BehaviorEventName.ActionAbandoned, runScreen, {
      action: BehaviorAction.DiscoveryRun,
      attemptId: runAttemptId,
      durationMs: Math.round(performance.now() - runStartedAt),
    })
  }

  async function start(payload: {
    first_name: string
    last_name: string
    dob: string
    gender: string | null
    address1: string | null
    city: string | null
    state: string | null
    postal_code: string | null
    ssn: string | null
    location_npi?: string | null
    patient_uuid?: string | null

    // VH-3175: Y-m-d; the same date the lookup sends to verify.
    date_of_service?: string | null
  }): Promise<DiscoverySession> {
    loading.value = true
    chatMessages.value = []
    clearPoll()
    pollGeneration++
    pollTimedOut.value = false
    pollFailed.value = false
    pollDeadlineAt = null

    beginDiscoveryRun()

    try {
      // Same 195s ceiling as verify() (see verification.ts). Production data
      // (stedi_api_logs) shows this endpoint's median call already spends ~30s
      // waiting on StediService's concurrency-slot funnel before Stedi is even
      // called -- routine contention, not a rare tail case.
      const { data } = await api.post('/insurance/discover', payload, { timeout: 195000 })

      session.value = data
      history.value.unshift(data)
      if (data.status === 'pending')
        schedulePoll(data.uuid)
      else

        // Settled without ever polling. Emitting for a 'pending' response here
        // would record a submit time as a finish time and make every discovery
        // that actually took a minute look instantaneous.
        completeDiscoveryRun(data.status)

      return data
    } catch (error) {
      abandonDiscoveryRun()
      throw error
    } finally {
      loading.value = false
    }
  }

  async function fetchSession(uuid: string): Promise<DiscoverySession> {
    // Same 195s ceiling as verify() (see verification.ts) -- this hits the same
    // Stedi-backed discovery status endpoint as poll() below.
    const { data } = await api.get(`/insurance/discover/${uuid}`, { timeout: 195000 })

    return data.data ?? data
  }

  async function poll(uuid: string): Promise<void> {
    const generation = pollGeneration

    try {
      // Same 195s ceiling as verify() -- and deliberately NOT shortened despite this being
      // a polling loop. Production data (stedi_api_logs) shows discovery_poll's p95 is ~63s
      // and p99 ~98s, so a short timeout here would trip the catch below routinely, not
      // rarely. The catch stops the polling loop outright on ANY error with no retry
      // (VH-2134 / Rollbar #1694, still open) -- a short timeout would make that bug fire
      // far more often, not less.
      //
      // isPollRequest: without it the global 429 handler toasts and returns a promise that
      // never settles, so neither the catch below nor the deadline ever ends the spinner.
      const { data } = await api.get(`/insurance/discover/${uuid}`, { timeout: 195000, isPollRequest: true })

      // A start()/selectSession()/reset() call landed while this request was in flight --
      // the user has moved on from this session, so don't clobber session.value / pollTimedOut,
      // and don't re-arm the poll loop for a session nobody is looking at.
      if (generation !== pollGeneration)
        return

      session.value = data

      const idx = history.value.findIndex(s => s.uuid === uuid)
      if (idx !== -1)
        history.value[idx] = data

      if (data.status === 'pending') {
        if (pollDeadlineAt !== null && Date.now() >= pollDeadlineAt) {
          pollTimedOut.value = true
          clearPoll()
          abandonDiscoveryRun()

          return
        }
        schedulePoll(uuid)
      } else {
        pollDeadlineAt = null
        completeDiscoveryRun(data.status)
      }
    } catch {
      if (generation !== pollGeneration)
        return

      pollFailed.value = true
      clearPoll()
      abandonDiscoveryRun()
    }
  }

  async function ask(question: string): Promise<void> {
    if (!session.value?.bedrock_session_uuid)
      return
    chatLoading.value = true

    chatMessages.value.push({
      uuid: crypto.randomUUID(),
      role: 'user',
      content: question,
      created_at: new Date().toISOString(),
    })

    try {
      const { data } = await api.post(`/insurance/chat/${session.value.bedrock_session_uuid}`, { question })

      chatMessages.value.push(data.message)
    } catch {
      chatMessages.value.pop()
      throw new Error('Chat failed')
    } finally {
      chatLoading.value = false
    }
  }

  /**
   * Why the current discovery run ended without a result, or null while it is still running
   * or finished fine. 'error' is the server marking the session failed; 'timeout' and
   * 'poll_failed' are this store giving up on the poll with the session still pending.
   */
  const failure = computed<DiscoveryFailure | null>(() => {
    if (session.value?.status === 'error')
      return 'error'
    if (pollFailed.value)
      return 'poll_failed'
    if (pollTimedOut.value)
      return 'timeout'

    return null
  })

  function schedulePoll(uuid: string) {
    clearPoll()
    if (pollDeadlineAt === null)
      pollDeadlineAt = Date.now() + POLL_DEADLINE_MS
    pollTimer = setTimeout(() => poll(uuid), 2500)
  }

  function clearPoll() {
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  // Logout / account switch: reset() clears the selected session, this also drops the history list.
  function resetForLogout() {
    reset()
    history.value = []
    historyPagination.value = { current_page: 1, per_page: 25, total: 0, last_page: 1 }
    historyLoading.value = false
  }

  function reset() {
    abandonDiscoveryRun()
    clearPoll()
    pollGeneration++
    session.value = null
    chatMessages.value = []
    loading.value = false
    chatLoading.value = false
    pollTimedOut.value = false
    pollFailed.value = false
    pollDeadlineAt = null
  }

  return {
    session,
    history,
    historyPagination,
    loading,
    historyLoading,
    chatMessages,
    chatLoading,
    pollTimedOut,
    pollFailed,
    failure,
    start,
    ask,
    fetchHistory,
    fetchSession,
    selectSession,
    reset,
    resetForLogout,
  }
})
